// FicheDetailModal.jsx — Modal détail d'une fiche d'annuaire (Phase B)
// Affiche : photo membre, logo entreprise, infos complètes, actions (message, recommander, mise en relation, favori)
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import {
  X, Mail, Phone, Globe, Linkedin, Instagram, Facebook, MapPin, Heart, MessageCircle,
  Star, Award, UserPlus, Send, Loader2, ShieldOff, ShieldCheck, Eye, Tag,
} from 'lucide-react';

const CONTEXTES = [
  { value: 'business', label: 'Opportunité business' },
  { value: 'expertise', label: 'Demande d\'expertise' },
  { value: 'collaboration', label: 'Collaboration / partenariat' },
  { value: 'autre', label: 'Autre' },
];

export default function FicheDetailModal({ ficheId, initialFiche, isFav, onToggleFav, canModerate, onClose }) {
  const { user } = useAuth();
  const [fiche, setFiche] = useState(initialFiche);
  const [recos, setRecos] = useState([]);
  const [loadingRecos, setLoadingRecos] = useState(true);
  const [tab, setTab] = useState('details');
  const [showReco, setShowReco] = useState(false);
  const [showMR, setShowMR] = useState(false);
  const [showMsg, setShowMsg] = useState(false);

  const isMine = fiche?.user_id === user?.id;

  // Fetch full fiche + recos + track view
  useEffect(() => {
    (async () => {
      try {
        const [f, r] = await Promise.all([
          api.get(`/annuaire/${ficheId}`),
          api.get(`/networking/${ficheId}/recommandations`),
        ]);
        setFiche(f.data);
        setRecos(r.data);
        // Tracker la vue (no-op si déjà vue aujourd'hui ou self-view)
        api.post(`/networking/${ficheId}/vue`).catch(() => {});
      } catch { /* keep initial fiche */ }
      finally { setLoadingRecos(false); }
    })();
  }, [ficheId]);

  const trackClick = async (type) => {
    if (isMine) return;
    api.post(`/networking/${ficheId}/clic`, { type }).catch(() => {});
  };

  const handleModerate = async (action) => {
    if (action === 'feature' && !window.confirm('Mettre en avant cette fiche ?')) return;
    if (action === 'suspend' && !window.confirm('Suspendre cette fiche ?')) return;
    try {
      const body = {};
      if (action === 'feature') body.mise_en_avant = !fiche.mise_en_avant;
      if (action === 'suspend') body.statut = fiche.statut === 'suspendu' ? 'actif' : 'suspendu';
      const { data } = await api.patch(`/annuaire/${ficheId}/moderation`, body);
      setFiche(data);
      toast.success('Mise à jour');
    } catch { toast.error('Erreur'); }
  };

  if (!fiche) return null;

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-md flex items-start justify-end fade-in" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl h-screen overflow-y-auto shadow-2xl" data-testid="fiche-detail-modal">

        {/* Hero couleur asso */}
        <div className="relative p-7 sm:p-9 text-white" style={{ background: `linear-gradient(135deg, var(--theme-primary) 0%, color-mix(in srgb, var(--theme-primary) 60%, black) 100%)` }}>
          <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 grid place-items-center" data-testid="fiche-close"><X className="w-4 h-4" /></button>

          <div className="flex items-start gap-4 mb-5">
            {fiche.photo_membre ? (
              <img src={fiche.photo_membre} alt={fiche.prenom} className="w-20 h-20 rounded-full object-cover ring-4 ring-white/30 shadow-lg shrink-0" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-white grid place-items-center font-display font-black text-2xl shrink-0 ring-4 ring-white/30 shadow-lg" style={{ color: 'var(--theme-primary)' }}>
                {(fiche.prenom?.[0] || '?') + (fiche.nom?.[0] || '')}
              </div>
            )}
            {fiche.logo_url && (
              <img src={fiche.logo_url} alt={fiche.nom_entreprise} className="w-14 h-14 rounded-xl object-contain bg-white p-1 shadow-sm shrink-0 -ml-2 mt-2" />
            )}
          </div>

          <div className="mb-2">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight" data-testid="fiche-entreprise-name">{fiche.nom_entreprise}</h2>
          </div>
          {(fiche.prenom || fiche.nom) && (
            <div className="text-base text-white/85 mb-2">
              {fiche.prenom} {fiche.nom}{fiche.metier && ` · ${fiche.metier}`}
            </div>
          )}
          <div className="flex items-center gap-2 text-xs text-white/70 flex-wrap">
            <span className="px-2.5 py-1 rounded-full bg-white/15 backdrop-blur font-bold uppercase tracking-[0.14em]">{fiche.secteur}</span>
            {fiche.ville && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {fiche.ville}</span>}
            {fiche.mise_en_avant && <span className="flex items-center gap-1"><Star className="w-3 h-3" /> En avant</span>}
            {canModerate && (
              <span className="flex items-center gap-1 ml-auto"><Eye className="w-3 h-3" /> {fiche.vues || 0} vues</span>
            )}
          </div>
        </div>

        {/* Actions (membre, sauf si c'est ma fiche) */}
        {!isMine && (
          <div className="px-7 sm:px-9 py-4 bg-white border-b border-[#E2E8F0] flex gap-2 flex-wrap" data-testid="fiche-actions">
            <button onClick={() => setShowMsg(true)} className="btn-primary !text-xs" data-testid="action-message"><MessageCircle className="w-3.5 h-3.5" /> Message</button>
            <button onClick={() => setShowReco(true)} className="btn-secondary !text-xs" data-testid="action-recommander"><Award className="w-3.5 h-3.5" /> Recommander</button>
            <button onClick={() => setShowMR(true)} className="btn-secondary !text-xs" data-testid="action-mise-en-relation"><UserPlus className="w-3.5 h-3.5" /> Mise en relation</button>
            <button onClick={onToggleFav} className={`btn-secondary !text-xs ${isFav ? '!text-[#DC2626] !border-[#FECACA]' : ''}`} data-testid="action-favori">
              <Heart className="w-3.5 h-3.5" fill={isFav ? 'currentColor' : 'none'} /> {isFav ? 'Favori' : 'Ajouter aux favoris'}
            </button>
          </div>
        )}

        {/* Actions modération */}
        {canModerate && (
          <div className="px-7 sm:px-9 py-3 bg-[#F9F9F8] border-b border-[#E2E8F0] flex gap-2 flex-wrap items-center">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mr-2">Modération</span>
            <button onClick={() => handleModerate('feature')} className="btn-ghost !text-xs" data-testid="mod-feature">
              <Star className="w-3 h-3" /> {fiche.mise_en_avant ? 'Retirer mise en avant' : 'Mettre en avant'}
            </button>
            <button onClick={() => handleModerate('suspend')} className="btn-ghost !text-xs" data-testid="mod-suspend">
              {fiche.statut === 'suspendu' ? <><ShieldCheck className="w-3 h-3" /> Réactiver</> : <><ShieldOff className="w-3 h-3" /> Suspendre</>}
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-[#E2E8F0] px-5">
          <TabBtn active={tab === 'details'} onClick={() => setTab('details')}>Détails</TabBtn>
          <TabBtn active={tab === 'recos'} onClick={() => setTab('recos')} badge={recos.length}>Recommandations</TabBtn>
        </div>

        {/* Contenu */}
        <div className="p-7 sm:p-9">
          {tab === 'details' ? (
            <>
              {fiche.description && (
                <div className="mb-6">
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">À propos</div>
                  <p className="text-sm text-[#0F172A] leading-relaxed whitespace-pre-wrap">{fiche.description}</p>
                </div>
              )}
              {fiche.mots_cles && (
                <div className="mb-6">
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Mots-clés</div>
                  <div className="flex flex-wrap gap-1.5">
                    {fiche.mots_cles.split(',').map((m, i) => (
                      <span key={i} className="badge badge-neutral"><Tag className="w-2.5 h-2.5" /> {m.trim()}</span>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-3">Coordonnées</div>
                <div className="space-y-2">
                  {fiche.email_pro && <ContactLink onClick={() => trackClick('email')} href={`mailto:${fiche.email_pro}`} icon={Mail} label={fiche.email_pro} testid="link-email" />}
                  {fiche.telephone_pro && <ContactLink onClick={() => trackClick('telephone')} href={`tel:${fiche.telephone_pro}`} icon={Phone} label={fiche.telephone_pro} testid="link-tel" />}
                  {fiche.site_web && <ContactLink onClick={() => trackClick('site_web')} href={ensureUrl(fiche.site_web)} target="_blank" icon={Globe} label={fiche.site_web} testid="link-web" />}
                  {fiche.linkedin && <ContactLink onClick={() => trackClick('linkedin')} href={ensureUrl(fiche.linkedin)} target="_blank" icon={Linkedin} label="LinkedIn" />}
                  {fiche.instagram && <ContactLink onClick={() => trackClick('instagram')} href={ensureUrl(fiche.instagram)} target="_blank" icon={Instagram} label="Instagram" />}
                  {fiche.facebook && <ContactLink onClick={() => trackClick('facebook')} href={ensureUrl(fiche.facebook)} target="_blank" icon={Facebook} label="Facebook" />}
                </div>
              </div>
            </>
          ) : (
            <RecosList recos={recos} loading={loadingRecos} onDelete={(id) => setRecos((r) => r.filter((x) => x.id !== id))} userId={user?.id} />
          )}
        </div>

        {showReco && <RecoModal ficheId={ficheId} onClose={(saved) => { setShowReco(false); if (saved) { setRecos((r) => [saved, ...r.filter((x) => x.from_user_id !== user.id)]); } }} />}
        {showMR && <MRModal ficheId={ficheId} ficheName={fiche.nom_entreprise} onClose={() => setShowMR(false)} />}
        {showMsg && <MsgModal toUserId={fiche.user_id} toName={`${fiche.prenom} ${fiche.nom}`} onClose={() => setShowMsg(false)} />}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, badge }) {
  return (
    <button onClick={onClick} className={`relative px-4 py-3 text-sm font-semibold transition-colors ${active ? 'text-[#0F172A]' : 'text-[#94A3B8] hover:text-[#475569]'}`}>
      {children}
      {badge !== undefined && badge > 0 && <span className="ml-1.5 text-[10px] font-black px-1.5 py-0.5 rounded-full bg-[#F1F5F9] text-[#475569]">{badge}</span>}
      {active && <span className="absolute -bottom-px inset-x-0 h-0.5 bg-[var(--theme-primary)]" />}
    </button>
  );
}

function ContactLink({ href, icon: Ic, label, onClick, target, testid }) {
  return (
    <a href={href} target={target} rel="noreferrer" onClick={onClick}
       className="flex items-center gap-3 p-3 rounded-xl border border-[#E2E8F0] hover:border-[var(--theme-primary)] hover:bg-[#F9F9F8] transition-colors group"
       data-testid={testid}>
      <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0 group-hover:bg-[var(--theme-primary-soft)] bg-[#F1F5F9]" style={{ color: 'var(--theme-primary)' }}>
        <Ic className="w-4 h-4" />
      </div>
      <span className="text-sm font-medium text-[#0F172A] truncate">{label}</span>
    </a>
  );
}

function RecosList({ recos, loading, onDelete, userId }) {
  if (loading) return <div className="py-8 grid place-items-center"><Loader2 className="w-4 h-4 animate-spin text-[#94A3B8]" /></div>;
  if (recos.length === 0) {
    return (
      <div className="text-center py-12">
        <Award className="w-8 h-8 mx-auto mb-3 text-[#CBD5E1]" />
        <p className="text-sm text-[#475569]">Aucune recommandation pour cette fiche.</p>
        <p className="text-xs text-[#94A3B8] mt-1">Soyez le premier à recommander !</p>
      </div>
    );
  }
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer votre recommandation ?')) return;
    try { await api.delete(`/networking/recommandations/${id}`); onDelete(id); toast.success('Supprimée'); }
    catch { toast.error('Erreur'); }
  };
  return (
    <div className="space-y-3" data-testid="recos-list">
      {recos.map((r) => (
        <div key={r.id} className="bg-[#F9F9F8] border border-[#E2E8F0] rounded-2xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-[#0F172A]">{r.from_prenom} {r.from_nom}</div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[#94A3B8]">{new Date(r.created_at).toLocaleDateString('fr-FR')}</span>
              {r.from_user_id === userId && (
                <button onClick={() => handleDelete(r.id)} className="text-[#DC2626] hover:underline text-[10px]" data-testid={`reco-delete-${r.id}`}>Supprimer</button>
              )}
            </div>
          </div>
          <p className="text-sm text-[#0F172A] leading-relaxed whitespace-pre-wrap">{r.texte}</p>
        </div>
      ))}
    </div>
  );
}

function RecoModal({ ficheId, onClose }) {
  const [texte, setTexte] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const { data } = await api.post(`/networking/${ficheId}/recommander`, { texte });
      toast.success('Recommandation publiée');
      onClose(data);
    } catch (err) { toast.error(err?.response?.data?.error || 'Erreur'); setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-[#0F172A]/60 backdrop-blur-md grid place-items-center p-4 fade-in" onClick={() => onClose(null)}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl p-7 max-w-md w-full" data-testid="reco-modal">
        <h3 className="font-display text-2xl font-bold text-[#0F172A] mb-2">
          Recommander <span className="italic font-medium" style={{ color: 'var(--theme-primary)' }}>ce membre.</span>
        </h3>
        <p className="text-sm text-[#475569] mb-5">Partagez votre expérience avec ce membre. La recommandation sera publique pour tous les membres de l'asso.</p>
        <textarea required minLength={10} maxLength={1000} rows={5} value={texte} onChange={(e) => setTexte(e.target.value)} className="ap-input" placeholder="Ex: Excellent boulanger, pain bio de qualité, service au top !" data-testid="reco-text" />
        <div className="text-right text-xs text-[#94A3B8] mt-1">{texte.length}/1000</div>
        <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-[#F1F5F9]">
          <button type="button" onClick={() => onClose(null)} className="btn-secondary">Annuler</button>
          <button type="submit" disabled={saving || texte.length < 10} className="btn-primary" data-testid="reco-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Publier'}</button>
        </div>
      </form>
    </div>
  );
}

function MRModal({ ficheId, ficheName, onClose }) {
  const [form, setForm] = useState({ contexte: 'business', message: '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post(`/networking/${ficheId}/mise-en-relation`, form); toast.success('Demande envoyée'); onClose(); }
    catch (err) { toast.error(err?.response?.data?.error || 'Erreur'); setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-[#0F172A]/60 backdrop-blur-md grid place-items-center p-4 fade-in" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl p-7 max-w-md w-full" data-testid="mr-modal">
        <h3 className="font-display text-2xl font-bold text-[#0F172A] mb-2">
          Demande de <span className="italic font-medium" style={{ color: 'var(--theme-primary)' }}>mise en relation.</span>
        </h3>
        <p className="text-sm text-[#475569] mb-5">À : <strong>{ficheName}</strong></p>
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Contexte</label>
            <select value={form.contexte} onChange={(e) => setForm({ ...form, contexte: e.target.value })} className="ap-input" data-testid="mr-contexte">
              {CONTEXTES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Votre message</label>
            <textarea required minLength={10} maxLength={1000} rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="ap-input" placeholder="Bonjour, j'aimerais discuter de…" data-testid="mr-message" />
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-[#F1F5F9]">
          <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="mr-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Envoyer</>}</button>
        </div>
      </form>
    </div>
  );
}

function MsgModal({ toUserId, toName, onClose }) {
  const [contenu, setContenu] = useState('');
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!toUserId) return toast.error('Membre non joignable (pas de compte)');
    setSending(true);
    try { await api.post('/messages', { to_user_id: toUserId, contenu }); toast.success('Message envoyé'); onClose(); }
    catch (err) { toast.error(err?.response?.data?.error || 'Erreur'); setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-[#0F172A]/60 backdrop-blur-md grid place-items-center p-4 fade-in" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl p-7 max-w-md w-full" data-testid="msg-modal">
        <h3 className="font-display text-2xl font-bold text-[#0F172A] mb-2">
          Message à <span className="italic font-medium" style={{ color: 'var(--theme-primary)' }}>{toName || 'ce membre'}</span>
        </h3>
        <p className="text-xs text-[#475569] mb-5">Le message sera disponible dans la messagerie interne.</p>
        {!toUserId ? (
          <div className="text-sm text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A] rounded-xl p-3 mb-4">Ce membre n'a pas encore de compte utilisateur. Utilisez "Mise en relation" pour passer par le bureau.</div>
        ) : (
          <textarea required minLength={2} rows={5} value={contenu} onChange={(e) => setContenu(e.target.value)} className="ap-input" placeholder="Tapez votre message…" data-testid="msg-text" />
        )}
        <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-[#F1F5F9]">
          <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
          <button type="submit" disabled={sending || !toUserId} className="btn-primary" data-testid="msg-submit">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Envoyer</>}</button>
        </div>
      </form>
    </div>
  );
}

function ensureUrl(s) {
  if (!s) return '#';
  return s.startsWith('http') ? s : `https://${s}`;
}
